
import { defineComponent } from 'vue';
import Form from '@/shared/mixins/Form';
import { required, email, minLength, maxLength, url, sameAs, helpers } from '@vuelidate/validators';
import { estateAgencyService } from '@/estate-agency/EstateAgencyService';
import { estateAgentService } from '@/estate-agent/EstateAgentService';
import { Address } from '@/shared/models/Address';
import { Point } from '@/shared/models/Point';
import MapPosition from '@/shared/components/MapPosition.vue';
import Cropper from '@/shared/components/Cropper.vue';
import { UploadFile } from '@/shared/models/UploadFile';
import ResizableImage from '@/shared/components/ResizableImage.vue';
import { deepCopy } from '@/utils';
import useVuelidate from '@vuelidate/core';
import config from '@/shared/constants/config';
import PhonePrefix from '@/shared/components/PhonePrefix.vue';
import phoneNumber from '@/shared/helpers/phoneNumberValidator';
import { googleTagManagerService } from '@/shared/services/GoogleTagManagerService';

export default defineComponent({
  name: 'RegistrationAgent',
  mixins: [Form],
  setup() {
    return { v$: useVuelidate() };
  },
  components: {
    MapPosition,
    ResizableImage,
    Cropper,
    PhonePrefix,
  },
  props: {
    isCompetition: Boolean,
    competition: String,
    competitionTermsUrl: String,
  },
  data() {
    return {
      domain: config.DOMAIN,
      privacyUrl: config.PRIVACY_URL,
      generalTermsUrl: config.GENERAL_TERMS_URL,
      address: null as null | Address,
      showAddressForm: false,
      initialPosition: null as null | Point,
      estateAgencyImage: {} as null | UploadFile,
      estateAgentImage: {} as null | UploadFile,
      showEstateAgencyImageModal: false,
      showEstateAgentImageModal: false,
      idNumberValidationData: null,
      form: {
        title: null as null | string,
        idNumber: null as null | string,
        taxIdNumber: null as null | string,
        vatIdNumber: null as null | string,
        agencyEmail: null as null | string,
        agencyPhone: null as null | string,
        website: null as null | string,
        logo: null as null | string,
        address: null as null | string,
        street: null as null | string,
        city: null as null | string,
        zip: null as null | string,
        countryCode: 'sk',
        latitude: null as null | number,
        longitude: null as null | number,
        isInvoiceAddressDifferent: false,
        invoiceStreet: null as null | string,
        invoiceCity: null as null | string,
        invoiceZip: null as null | string,
        invoiceCountryCode: 'sk',
        phoneNumbers: [] as string[],

        firstName: null,
        lastName: null,
        email: null,
        confirmEmail: null,
        phoneNumberPrefix: config.DEFAULT_PHONE_PREFIX,
        phoneNumber: null,
        password: null,
        confirmPassword: null,
        generalTermsAgreement: false,
        personalAgreement: false,
        competitionTermsAgreement: false,
        languageId: (window as never)['languageId'],
        photo: null as null | string,
        backOfficeConsumerKey: null as null | string,
        backOfficeConsumerSecret: null as null | string,
      },
    };
  },
  validations() {
    const validations = {
      form: {
        title: { required },
        idNumber: {
          required,
          maxLength: maxLength(20),
          isUnique: helpers.withAsync(async (idNumber) => {
            if (idNumber) {
              try {
                const result = await estateAgencyService.checkIdNumber({ idNumber });
                this.idNumberValidationData = result.data;
                return result.data.isValid;
              } catch (error) {
                console.error(error);
                return false;
              }
            } else {
              return false;
            }
          }),
        },
        street: { required },
        city: { required },
        zip: { required },
        countryCode: {},
        taxIdNumber: { maxLength: maxLength(20) },
        vatIdNumber: { maxLength: maxLength(20) },
        agencyEmail: { required, email },
        agencyPhone: { required, phoneNumber: phoneNumber() },
        website: { url },
        address: { required },
        latitude: { required },
        longitude: { required },
        invoiceStreet: {},
        invoiceCity: {},
        invoiceZip: {},
        invoiceCountryCode: {},
        phoneNumbers: {},

        firstName: { required },
        lastName: { required },
        email: { required, email },
        phoneNumberPrefix: { required },
        phoneNumber: { required, phoneNumber: phoneNumber(this.form.phoneNumberPrefix) },
        confirmEmail: { required, sameAsEmail: sameAs(this.form.email) },
        password: { required, minLength: minLength(8) }, // https://jsfiddle.net/sg2zd9mf/
        confirmPassword: { required, sameAsPassword: sameAs(this.form.password) },
        generalTermsAgreement: { sameAs: sameAs(true) },
        personalAgreement: { sameAs: sameAs(true) },
        competitionTermsAgreement: {},
        backOfficeConsumerKey: {},
        backOfficeConsumerSecret: {},
      },
    };
    if (this.isCompetition) {
      validations.form.competitionTermsAgreement = { sameAs: sameAs(true) };
    }
    return validations;
  },
  methods: {
    removePhoneNumber(index: number) {
      this.form.phoneNumbers.splice(index, 1);
    },
    async register() {
      if (!this.validate()) return;

      try {
        this.startLoading();
        const cloneForm = deepCopy(this.form);
        if (this.isCompetition) {
          cloneForm.competition = this.competition;
        }

        if (cloneForm.agencyPhone) {
          cloneForm.phoneNumbers.unshift(cloneForm.agencyPhone);
        }
        if (!cloneForm.isInvoiceAddressDifferent) {
          cloneForm.invoiceStreet = cloneForm.street;
          cloneForm.invoiceCity = cloneForm.city;
          cloneForm.invoiceZip = cloneForm.zip;
          cloneForm.invoiceCountryCode = cloneForm.countryCode;
        }
        if (cloneForm.phoneNumberPrefix && cloneForm.phoneNumberPrefix.value) {
          cloneForm.phoneNumberPrefix = cloneForm.phoneNumberPrefix.value;
        }
        await estateAgencyService.register(cloneForm);
        this.success();
        googleTagManagerService.send({
          event: 'registracia_PO',
        });
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.endLoading();
      }
    },
    async handleEstateAgencyImageUpload() {
      const ref = this.$refs.estateAgencyImage as any;
      const files = ref.files as string[] | Blob[];
      const file = files[0];
      await this.uploadEstateAgencyImage(file);
      ref.value = null;
    },
    async uploadEstateAgencyImage(file: any) {
      this.showEstateAgencyImageModal = false;
      this.estateAgencyImage = {} as UploadFile;
      this.estateAgencyImage.isUploading = true;
      this.estateAgencyImage.uploadPercentage = 0;
      this.estateAgencyImage.file = file.name;
      const formData = new FormData();
      formData.append('file', file, file.name);

      try {
        const result = await estateAgencyService.uploadLogo(formData, (progressEvent: any) => {
          if (this.estateAgencyImage) {
            this.estateAgencyImage.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100).toString());
          }
        });
        this.estateAgencyImage.file = result.data.logo;
        this.estateAgencyImage.url = result.data.url;
        this.estateAgencyImage.isSuccess = true;
        this.form.logo = result.data.logo;
      } catch (error) {
        this.estateAgencyImage.isError = true;
        this.estateAgencyImage.text = 'Error';
        const errors = this.getApiErrors(error);
        this.estateAgencyImage.text = errors[0].message;
      } finally {
        this.estateAgencyImage.isUploading = false;
      }
    },
    async handleEstateAgentImageUpload() {
      const ref = this.$refs.estateAgentImage as any;
      const files = ref.files as string[] | Blob[];
      const file = files[0];
      await this.uploadEstateAgentImage(file);
      ref.value = null;
    },
    async uploadEstateAgentImage(file: any) {
      this.showEstateAgentImageModal = false;
      this.estateAgentImage = {} as UploadFile;
      this.estateAgentImage.isUploading = true;
      this.estateAgentImage.uploadPercentage = 0;
      this.estateAgentImage.file = file.name;
      const formData = new FormData();
      formData.append('file', file, file.name);

      try {
        const result = await estateAgentService.uploadPhoto(formData, (progressEvent: any) => {
          if (this.estateAgentImage) {
            this.estateAgentImage.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100).toString());
          }
        });
        this.estateAgentImage.file = result.data.photo;
        this.estateAgentImage.url = result.data.url;
        this.estateAgentImage.isSuccess = true;
        this.form.photo = result.data.photo;
      } catch (error) {
        this.estateAgentImage.isError = true;
        this.estateAgentImage.text = 'Error';
        const errors = this.getApiErrors(error);
        this.estateAgentImage.text = errors[0].message;
      } finally {
        this.estateAgentImage.isUploading = false;
      }
    },
    editEstateAgencyFile() {
      this.showEstateAgencyImageModal = true;
    },
    editEstateAgentFile() {
      this.showEstateAgentImageModal = true;
    },
    deleteEstateAgencyFile() {
      this.estateAgencyImage = {} as UploadFile;
      this.form.logo = null;
    },
    deleteEstateAgentFile() {
      this.estateAgentImage = {} as UploadFile;
      this.form.photo = null;
    },
    onAddressChanged(address: Address) {
      this.address = address;
      if (address) {
        this.form.street = address.streetNumber ? address.street + ' ' + address.streetNumber : address.street;
        this.form.city = address.city;
        this.form.zip = address.zip;
        this.form.countryCode = address.countryCode;
        this.form.latitude = address.latitude;
        this.form.longitude = address.longitude;
        this.form.address = address.address;
      } else {
        this.form.street = '';
        this.form.city = '';
        this.form.zip = '';
        this.form.countryCode = '';
        this.form.latitude = null;
        this.form.longitude = null;
        this.form.address = null;
      }
      if (!this.showAddressForm) {
        this.showAddressForm = !(this.form.street && this.form.city && this.form.zip);
        const streetValidationControl = (this.v$.form as any).street;
        if (streetValidationControl) {
          streetValidationControl.$touch();
        }
        const cityValidationControl = (this.v$.form as any).city;
        if (cityValidationControl) {
          cityValidationControl.$touch();
        }
        const zipValidationControl = (this.v$.form as any).zip;
        if (zipValidationControl) {
          zipValidationControl.$touch();
        }
      }
      const validationControl = (this.v$.form as any).address;
      if (validationControl) {
        validationControl.$touch();
      }
    },
  },
});
